import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Table,
  Space,
  Row,
  Modal,
  Form,
  Input,
  Checkbox,
  Popconfirm,
} from 'antd';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
import { last, first } from 'lodash';

import Pagination from '../../Components/Pagination';
import PageLayout from '../../Layout';
import { addOrder, getOrders, deleteOrder, updateOrder } from '../../Services';
import { message } from '../../Utils';

const Orders = () => {
  const [isVisibleModal, setVisibleModal] = useState(false);
  const [form] = Form.useForm();
  const [orders, setOrders] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    getAllOrders()
  }, []);

  const data = useMemo(() => {
    if (!orders) return [];
    return orders.map(doc => ({ ...doc.data(), key: doc.id }));
  }, [orders]);

  const getAllOrders = (options) => {
    getOrders(options).then(docs => {
      setOrders(docs);
    });
  }

  const handleAddOrder = () => {
    form
      .validateFields()
      .then(values => {
        addOrder({
          name: values.name,
          active: values.active,
          place: parseInt(values.place, 10),
        }).then(() => {
          form.resetFields();
          setVisibleModal(false);
          getAllOrders();
          message.success('Item is added.');
        }).catch((err) => {
          message.error(err.message);
        });
      })
      .catch(() => {
        message.error('Something went wrong.');
      });
  }

  const handleUpdateOrder = () => {
    form
      .validateFields()
      .then(values => {
        updateOrder(selectedItem.key, {
          name: values.name,
          active: values.active,
          place: parseInt(values.place, 10),
        }).then(() => {
          form.resetFields();
          setVisibleModal(false);
          getAllOrders();
          message.success('Item is updated.');
        }).catch((err) => {
          message.error(err.message);
        });
      })
      .catch(() => {
        message.error('Something went wrong.')
      });
  }

  const addOrUpdateOrder = () => {
    if (selectedItem) {
      handleUpdateOrder();
    } else {
      handleAddOrder();
    }
  }

  const handleOpenAddModal = () => {
    setVisibleModal(true);
    form.setFieldsValue({ active: true });
  }

  const handleCloseAddModal = () => {
    setVisibleModal(false);
    setSelectedItem(null);
    form.resetFields();
  }

  const onClickEdit = (item) => {
    form.setFieldsValue(item);
    setVisibleModal(true);
    setSelectedItem(item);
  }

  const onClickDelete = (item) => {
    deleteOrder(item.key).then(() => {
      getAllOrders();
      message.success('Item is deleted.');
    }).catch(() => {
      message.error('Something went wrong.');
    });
  }

  const handleChangePage = (action) => {
    if (action === 'next') {
      getAllOrders({ lastDoc: last(orders) });
    } else {
      getAllOrders({ firstDoc: first(orders) });
    }
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Place',
      dataIndex: 'place',
      key: 'place',
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (_, record) => (<Checkbox checked={record.active} />)
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditFilled />} onClick={() => onClickEdit(record)}>Edit</Button>
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this item?"
            onConfirm={() => onClickDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" icon={<DeleteFilled />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout title="Orders">
      <Row justify='end' align="middle" style={{ marginTop: 10, marginBottom: 10 }}>
        <Button type="primary" onClick={handleOpenAddModal}>Add Order</Button>
      </Row>
      <Table columns={columns} dataSource={data} pagination={{ position: ["none", "none"] }} />
      <Pagination onChange={handleChangePage} size={data.length} />
      {isVisibleModal && (
        <Modal
          visible={isVisibleModal}
          title={selectedItem ? "Update order" : "Create a new order"}
          okText={selectedItem ? "Update" : "Create"}
          cancelText="Cancel"
          onCancel={handleCloseAddModal}
          onOk={addOrUpdateOrder}
        >
          <Form
            form={form}
            layout="vertical"
            name="form_in_modal"
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please input the name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="place"
              label="Place"
              rules={[{ required: true, message: 'Please input the place' }]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              name="active"
              valuePropName='checked'
            >
              <Checkbox>
                Active
              </Checkbox>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </PageLayout>
  );
}

export default Orders;